// チャットルームAPIクライアント
// UNIFIED_DEVELOPMENT_GUIDE準拠

import api from '@/utils/api';
import type {
  ChatRoom,
  ChatRoomFormData,
  ChatRoomFilters,
  ChatRoomListResponse,
  ChatRoomResponse,
} from '../types';

// APIエンドポイントベースパス
const BASE_PATH = '/v3/chat/rooms';

// チャットルームAPI
export const chatRoomApi = {
  /**
   * チャットルーム一覧取得
   */
  list: async (filters?: ChatRoomFilters): Promise<ChatRoomListResponse> => {
    const params = new URLSearchParams();

    if (filters?.type) {
      const types = Array.isArray(filters.type) ? filters.type : [filters.type];
      params.append('type', types.join(','));
    }

    if (filters?.store_id) {
      params.append('store_id', String(filters.store_id));
    }

    if (filters?.search) {
      params.append('search', filters.search);
    }

    if (filters?.is_active !== undefined) {
      params.append('is_active', filters.is_active ? '1' : '0');
    }

    if (filters?.page) {
      params.append('page', String(filters.page));
    }

    if (filters?.per_page) {
      params.append('per_page', String(filters.per_page));
    }

    const response = await api.get<ChatRoomListResponse>(
      `${BASE_PATH}?${params.toString()}`
    );

    return response.data;
  },

  /**
   * チャットルーム詳細取得
   */
  get: async (id: number): Promise<ChatRoom> => {
    const response = await api.get<ChatRoomResponse>(`${BASE_PATH}/${id}`);
    return response.data.data;
  },

  /**
   * チャットルーム作成
   */
  create: async (data: ChatRoomFormData): Promise<ChatRoom> => {
    // バックエンドが期待するフィールド名に変換
    const payload = {
      type: data.room_type,
      name: data.name,
      description: data.description || null,
      icon: data.icon || null,
      store_id: data.store_id || null,
      participants: data.member_ids || [],
    };

    const response = await api.post<ChatRoomResponse>(BASE_PATH, payload);
    return response.data.data;
  },

  /**
   * チャットルーム更新
   */
  update: async (id: number, data: Partial<ChatRoomFormData>): Promise<ChatRoom> => {
    const payload = {
      ...(data.room_type && { type: data.room_type }),
      ...(data.name && { name: data.name }),
      ...(data.description !== undefined && { description: data.description }),
      ...(data.icon !== undefined && { icon: data.icon }),
      ...(data.store_id !== undefined && { store_id: data.store_id }),
    };

    const response = await api.put<ChatRoomResponse>(`${BASE_PATH}/${id}`, payload);
    return response.data.data;
  },

  /**
   * チャットルーム削除
   */
  delete: async (id: number): Promise<void> => {
    await api.delete(`${BASE_PATH}/${id}`);
  },

  /**
   * チャットルーム参加
   */
  join: async (id: number): Promise<void> => {
    await api.post(`${BASE_PATH}/${id}/join`);
  },

  /**
   * チャットルーム退出
   */
  leave: async (id: number): Promise<void> => {
    await api.post(`${BASE_PATH}/${id}/leave`);
  },

  /**
   * 既読マーク
   */
  markAsRead: async (id: number): Promise<void> => {
    await api.post(`${BASE_PATH}/${id}/mark-read`);
  },
};
